import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import API_BASE_URL from "../config";

const InventoryManager = ({ companyId, companyName = "Company" }) => {
  const [activeTab, setActiveTab] = useState("stock-in"); // Default: Stock IN
  const [products, setProducts] = useState([]);
  const [branches, setBranches] = useState([]); // NEW
  const [selectedBranch, setSelectedBranch] = useState(""); // NEW: shared branch

  const [searchTerm, setSearchTerm] = useState(""); // NEW
  const [history, setHistory] = useState([]); // NEW
  const [showHistoryModal, setShowHistoryModal] = useState(false); // NEW
  const [selectedProduct, setSelectedProduct] = useState(null); // NEW

  // Enhanced product form
  const [form, setForm] = useState({
    name: "",
    description: "",
    per_unit_cost: "",
    total_amount: "", // NEW (for product creation)
    low_stock_threshold: 10,
  });

  const [stockForm, setStockForm] = useState({
    product_id: "",
    product_name: "",
    quantity: "",
    per_unit_cost: "",
    total_amount: "", // NEW
    notes: "",
    branch_id: "",
  });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  const API = `${API_BASE_URL}/companies/${companyId}/inventory`;
  useEffect(() => {
    if (companyName) {
      window.__COMPANY__ = { name: companyName };
    }
  }, [companyName]);
  // NEW: Fetch branches
  const fetchBranches = async () => {
    try {
      const res = await axios.get(`${API}/branches`);
      setBranches(res.data.branches);
    } catch (err) {
      console.error("Failed to load branches", err);
    }
  };
  // SweetAlert2 Helpers
  const showError = (text) =>
    Swal.fire({
      title: "Error",
      text,
      icon: "error",
      confirmButtonColor: "#d33",
    });
  const showSuccess = (text) =>
    Swal.fire({
      title: "Success",
      text,
      icon: "success",
      confirmButtonColor: "#3085d6",
    });
  /* --------------------------------------------------------------
   1. REPLACE the old showLowStock function
   -------------------------------------------------------------- */
  const showLowStock = (name, qty, location) => {
    const loc = location ? `Branch ID: ${location}` : "Company Level";

    Swal.fire({
      title: "Low Stock Alert!",
      html: `<strong>${name}</strong> is at <strong>${qty}</strong> units<br><small>${loc}</small>`,
      icon: "warning",
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 6000,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.addEventListener("mouseenter", Swal.stopTimer);
        toast.addEventListener("mouseleave", Swal.resumeTimer);
      },
    });
  };
  // Updated: Use selectedBranch instead of stockForm.branch_id
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/products`, {
        params: { page, branch_id: selectedBranch || undefined },
      });
      setProducts(res.data.products);

      res.data.products.forEach((p) => {
        if (p.low_stock_alert) {
          showLowStock(
            p.name,
            p.current_quantity,
            selectedBranch || "Company Level"
          );
        }
      });
    } catch (err) {
      showError(err.response?.data?.message || "Failed to load products");
    } finally {
      setLoading(false);
    }
  };
  // Updated: Load branches + products
  useEffect(() => {
    if (!companyId) return;
    fetchBranches();
    fetchProducts();
  }, [companyId, selectedBranch, page]);

  // Poll Alerts
  useEffect(() => {
    if (!companyId) return;
    fetchProducts();

    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/alerts`);
        res.data.alerts.forEach((alert) => {
          if (typeof alert === "string") {
            const match = alert.match(
              /Low Stock Alert: (.+) is at (\d+) units \((.+)\)/
            );
            if (match) showLowStock(match[1], match[2], match[3]);
          } else if (alert.type === "low_stock") {
            showLowStock(alert.name, alert.quantity, alert.location);
          }
        });
      } catch (err) {
        // Silent
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [companyId, page, stockForm.branch_id]);

  // Create Product
  const handleCreate = async (e) => {
    e.preventDefault();
    const unit = parseFloat(form.per_unit_cost) || 0;

    const payload = {
      ...form,
      per_unit_cost: unit,
    };
    try {
      await axios.post(`${API}/products`, payload);
      showSuccess("Product created successfully");
      setForm({
        name: "",
        description: "",
        per_unit_cost: "",
        total_amount: "",
        low_stock_threshold: 10,
      });
      fetchProducts();
    } catch (err) {
      showError(err.response?.data?.message || "Failed to create product");
    }
  };

  // Stock IN (Auto-create product if needed)
  const handleStockIn = async (e) => {
    e.preventDefault();
    const qty = parseFloat(stockForm.quantity);
    const unit = parseFloat(stockForm.per_unit_cost) || 0;
    const total = parseFloat(stockForm.total_amount) || qty * unit;

    if (qty <= 0) {
      showError("Quantity must be greater than 0");
      return;
    }
    const payload = {
      ...stockForm,
      quantity: qty,
      per_unit_cost: unit,
      amount: total, // ← Saved in inventory_transactions.amount
      product_name: stockForm.product_name.trim(),
      branch_id: selectedBranch || null,
    };
    try {
      await axios.post(`${API}/stock-in`, payload);
      showSuccess("Stock added successfully");
      setStockForm({
        product_id: "",
        product_name: "",
        quantity: "",
        per_unit_cost: "",
        total_amount: "",
        notes: "",
      });
      fetchProducts();
    } catch (err) {
      showError(err.response?.data?.message || "Stock in failed");
    }
  };

  // Stock OUT
  const handleStockOut = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/stock-out`, {
        ...stockForm,
        branch_id: selectedBranch || null,
        reference_type: "sale",
      });
      showSuccess("Stock deducted successfully");
      setStockForm({ ...stockForm, quantity: "", notes: "" });
      fetchProducts();
    } catch (err) {
      showError(err.response?.data?.message || "Insufficient stock");
    }
  };
  // === Helper: Sync calculations ===
  const updateStockCalculations = (field, value) => {
    const qty = parseFloat(stockForm.quantity) || 0;
    const unit = parseFloat(stockForm.per_unit_cost) || 0;
    const total = parseFloat(stockForm.total_amount) || 0;

    let newQty = qty,
      newUnit = unit,
      newTotal = total;

    if (field === "quantity") newQty = parseFloat(value) || 0;
    if (field === "per_unit_cost") newUnit = parseFloat(value) || 0;
    if (field === "total_amount") newTotal = parseFloat(value) || 0;

    // === AUTO-CALCULATE LOGIC ===
    if (newQty > 0 && newUnit > 0) {
      // Qty + Unit → Total
      newTotal = (newQty * newUnit).toFixed(2);
    } else if (newQty > 0 && newTotal > 0) {
      // Qty + Total → Unit
      newUnit = (newTotal / newQty).toFixed(2);
    } else if (newUnit > 0 && newTotal > 0) {
      // Unit + Total → Qty (floor)
      newQty = Math.floor(newTotal / newUnit);
    }

    // === DO NOT BLOCK INPUT ===
    // Always update the field user typed in
    setStockForm({
      ...stockForm,
      quantity: field === "quantity" ? value : newQty.toString(),
      per_unit_cost: field === "per_unit_cost" ? value : newUnit.toString(),
      total_amount: field === "total_amount" ? value : newTotal.toString(),
    });
  };
  const updateProductCalculations = (field, value) => {
    const total = parseFloat(form.total_amount) || 0;
    const unit = parseFloat(form.per_unit_cost) || 0;

    let newTotal = total,
      newUnit = unit;

    if (field === "total_amount") newTotal = parseFloat(value) || 0;
    if (field === "per_unit_cost") newUnit = parseFloat(value) || 0;

    setForm({
      ...form,
      per_unit_cost: newUnit,
      total_amount: newTotal,
    });
  };
  const getAvailableStock = () => {
    const product = products.find(
      (p) => p.id === parseInt(stockForm.product_id)
    );
    return product ? product.current_quantity : 0;
  };

  // === SEARCH & FILTER ===
  const filteredProducts = products.filter((p) =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // === EXPORT TO CSV ===
  const exportToCSV = () => {
    // 1. Get company name (passed as prop)
    const company = window.__COMPANY__ || { name: "Unknown Company" };

    // 2. Get branch name
    const branchName = selectedBranch
      ? branches.find((b) => b.id == selectedBranch)?.name || "Unknown Branch"
      : "All Branches (Company Level)";

    const headers = [
      "Product",
      "Stock",
      "Cost/Unit",
      "Threshold",
      "Status",
      "Branch",
    ];
    const rows = filteredProducts.map((p) => [
      p.name,
      p.current_quantity,
      `₹${parseFloat(p.per_unit_cost).toFixed(2)}`,
      p.low_stock_threshold,
      p.low_stock_alert ? "LOW" : "OK",
      branchName,
    ]);
    const csv = [
      [`Company: ${company.name}`],
      [`Branch: ${branchName}`],
      [""],
      headers,
      ...rows,
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `inventory_${company.name.replace(
      /[^a-z0-9]/gi,
      "_"
    )}_${branchName.replace(/[^a-z0-9]/gi, "_")}_${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // === STOCK HISTORY ===
  const fetchHistory = async (productId) => {
    try {
      const res = await axios.get(`${API}/products/${productId}/history`, {
        params: { branch_id: selectedBranch || undefined },
      });
      setHistory(res.data.history || []);
      setShowHistoryModal(true);
    } catch (err) {
      showError("Failed to load history");
    }
  };

  const openHistory = (product) => {
    setSelectedProduct(product);
    fetchHistory(product.id);
  };

  const closeHistory = () => {
    setShowHistoryModal(false);
    setSelectedProduct(null);
    setHistory([]);
  };
  return (
  <div className="inventory-modal-layout">
    {/* ==================== FIXED TOP NAVIGATION ==================== */}
    <div className="inventory-top-nav">
      {/* <h5 className="mb-0">Inventory — {companyName}</h5> */}
      <ul className="nav nav-tabs mt-3 gap-2">
        <li className="nav-item">
          <button
            className={`nav-link px-4 py-2 ${activeTab === "stock-in" ? "active" : ""}`}
            data-tab="stock-in"
            onClick={() => setActiveTab("stock-in")}
          >
            Stock IN
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link px-4 py-2 ${activeTab === "stock-out" ? "active" : ""}`}
            data-tab="stock-out"
            onClick={() => setActiveTab("stock-out")}
          >
            Stock OUT
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link px-4 py-2 ${activeTab === "products" ? "active" : ""}`}
            data-tab="products"
            onClick={() => setActiveTab("products")}
          >
            Products
          </button>
        </li>
      </ul>
    </div>

    {/* ==================== MAIN SCROLLABLE CONTENT ==================== */}
    <div className="inventory-main-content">
      <div className="container-fluid">
        {/* Branch Selector */}
        <div className="mb-4 bg-white p-4 rounded shadow-sm">
          <label className="form-label fw-bold text-dark">Select Branch</label>
          <select
            value={selectedBranch}
            onChange={(e) => {
              setSelectedBranch(e.target.value);
              setPage(1);
            }}
            className="form-select"
          >
            <option value="">All Branches (Company Level)</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>

        {/* ==================== STOCK IN TAB ==================== */}
        {activeTab === "stock-in" && (
          <div className="bg-white p-4 rounded shadow">
            <h2 className="h5 fw-bold text-success mb-4">Stock IN (Add)</h2>
            <form onSubmit={handleStockIn} className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Product Name (or select below)</label>
                <input
                  type="text"
                  placeholder="Enter new product name"
                  value={stockForm.product_name}
                  onChange={(e) => setStockForm({ ...stockForm, product_name: e.target.value })}
                  className="form-control"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Or Select Existing</label>
                <select
                  value={stockForm.product_id}
                  onChange={(e) =>
                    setStockForm({
                      ...stockForm,
                      product_id: e.target.value,
                      product_name: "",
                    })
                  }
                  className="form-select"
                >
                  <option value="">-- Select Product --</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} (₹{p.per_unit_cost})
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Quantity *</label>
                <input type="number" placeholder="Qty" value={stockForm.quantity} onChange={(e) => setStockForm({ ...stockForm, quantity: e.target.value })} className="form-control" required />
              </div>
              <div className="col-md-3">
                <label className="form-label">Per Unit Cost</label>
                <input type="number" placeholder="Cost" value={stockForm.per_unit_cost} onChange={(e) => setStockForm({ ...stockForm, per_unit_cost: e.target.value })} className="form-control" step="0.01" />
              </div>
              <div className="col-md-3">
                <label className="form-label">Total Amount</label>
                <input type="number" placeholder="₹ total" value={stockForm.total_amount} onChange={(e) => updateStockCalculations("total_amount", e.target.value)} className="form-control" step="0.01" />
              </div>
              <div className="col-md-3">
                <label className="form-label">Notes</label>
                <input placeholder="Notes" value={stockForm.notes} onChange={(e) => setStockForm({ ...stockForm, notes: e.target.value })} className="form-control" />
              </div>
              <div className="col-12">
                <button type="submit" className="btn btn-success w-100">Add Stock</button>
              </div>
            </form>
          </div>
        )}

        {/* ==================== STOCK OUT TAB ==================== */}
        {activeTab === "stock-out" && (
          <div className="bg-white p-4 rounded shadow">
            <h2 className="h5 fw-bold text-danger mb-4">Stock OUT (Deduct)</h2>
            <form onSubmit={handleStockOut} className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Product *</label>
                <select
                  value={stockForm.product_id}
                  onChange={(e) =>
                    setStockForm({
                      ...stockForm,
                      product_id: e.target.value,
                      quantity: "",
                    })
                  }
                  className="form-select"
                  required
                >
                  <option value="">-- Select Product --</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.current_quantity} left)
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Quantity *</label>
                <input
                  type="number"
                  placeholder="Qty"
                  value={stockForm.quantity}
                  onChange={(e) => {
                    const qty = e.target.value;
                    const available = products.find((p) => p.id === parseInt(stockForm.product_id))?.current_quantity || 0;
                    if (qty === "" || (parseInt(qty) > 0 && parseInt(qty) <= available)) {
                      setStockForm({ ...stockForm, quantity: qty });
                    }
                  }}
                  className={`form-control ${stockForm.quantity && parseInt(stockForm.quantity) > (products.find((p) => p.id === parseInt(stockForm.product_id))?.current_quantity || 0) ? "is-invalid" : ""}`}
                  min="1"
                  required
                />
                {stockForm.quantity && parseInt(stockForm.quantity) > (products.find((p) => p.id === parseInt(stockForm.product_id))?.current_quantity || 0) && (
                  <div className="invalid-feedback">
                    Only {products.find((p) => p.id === parseInt(stockForm.product_id))?.current_quantity} available
                  </div>
                )}
              </div>
              <div className="col-md-6">
                <label className="form-label">Notes</label>
                <input placeholder="Notes" value={stockForm.notes} onChange={(e) => setStockForm({ ...stockForm, notes: e.target.value })} className="form-control" />
              </div>
              <div className="col-12">
                <button
                  type="submit"
                  className="btn btn-danger w-100"
                  disabled={
                    !stockForm.product_id ||
                    !stockForm.quantity ||
                    parseInt(stockForm.quantity) > (products.find((p) => p.id === parseInt(stockForm.product_id))?.current_quantity || 0)
                  }
                >
                  Deduct Stock
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ==================== PRODUCTS TAB ==================== */}
        {activeTab === "products" && (
          <div>
            {/* Add New Product */}
            <div className="bg-white p-4 rounded shadow mb-4">
              <h2 className="h5 fw-bold text-primary mb-4">Add New Product</h2>
              <form onSubmit={handleCreate} className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-bold">Product Name <span className="text-danger">*</span></label>
                  <input type="text" placeholder="Enter product name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="form-control" required />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-bold">Description</label>
                  <input type="text" placeholder="Short description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="form-control" />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-bold">Per Unit Cost <span className="text-danger">*</span></label>
                  <input type="number" placeholder="₹ per unit" value={form.per_unit_cost} onChange={(e) => updateProductCalculations("per_unit_cost", e.target.value)} className="form-control" step="0.01" required />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-bold">Total Amount (info)</label>
                  <input type="number" placeholder="₹ total" value={form.total_amount} onChange={(e) => updateProductCalculations("total_amount", e.target.value)} className="form-control" step="0.01" />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-bold">Low Stock Threshold</label>
                  <input type="number" placeholder="Alert when stock ≤" value={form.low_stock_threshold} onChange={(e) => setForm({ ...form, low_stock_threshold: e.target.value })} className="form-control" min="1" />
                </div>
                <div className="col-12 mt-3">
                  <button type="submit" className="btn btn-primary w-100">Create Product</button>
                </div>
              </form>
            </div>

            {/* Search + Export */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="flex-grow-1 me-3">
                <input type="text" placeholder="Search products..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="form-control" />
              </div>
              <button onClick={exportToCSV} className="btn btn-outline-success">Export to CSV</button>
            </div>

            {/* Products Table */}
            <div className="bg-white rounded shadow overflow-hidden">
              <table className="table table-hover mb-0">
                <thead className="bg-light">
                  <tr>
                    <th>Name</th>
                    <th>Stock</th>
                    <th>Cost/Unit</th>
                    <th>Threshold</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="5" className="text-center py-4">Loading...</td></tr>
                  ) : filteredProducts.length === 0 ? (
                    <tr><td colSpan="5" className="text-center py-4 text-muted">{searchTerm ? "No matching products" : "No products"}</td></tr>
                  ) : (
                    filteredProducts.map((p) => (
                      <tr key={p.id} className={p.low_stock_alert ? "table-danger" : ""}>
                        <td>
                          <button onClick={() => openHistory(p)} className="btn btn-link p-0 text-start text-decoration-underline">
                            {p.name}
                          </button>
                        </td>
                        <td><strong>{p.current_quantity}</strong></td>
                        <td>₹{parseFloat(p.per_unit_cost).toFixed(2)}</td>
                        <td>{p.low_stock_threshold}</td>
                        <td>
                          {p.low_stock_alert ? (
                            <span className="badge bg-danger">LOW</span>
                          ) : (
                            <span className="badge bg-success">OK</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {filteredProducts.length > 0 && (
              <div className="d-flex justify-content-center mt-4 gap-3">
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="btn btn-sm btn-outline-secondary">Prev</button>
                <span className="align-self-center fw-bold">Page {page}</span>
                <button onClick={() => setPage((p) => p + 1)} className="btn btn-sm btn-outline-secondary">Next</button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* History Modal */}
      {showHistoryModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Stock History: <strong>{selectedProduct?.name}</strong>
                  {selectedBranch && ` (Branch: ${branches.find((b) => b.id == selectedBranch)?.name})`}
                </h5>
                <button onClick={closeHistory} className="btn-close"></button>
              </div>
              <div className="modal-body">
                {history.length === 0 ? (
                  <p className="text-muted text-center">No transactions yet.</p>
                ) : (
                  <table className="table table-sm">
                    <thead><tr><th>Date</th><th>Type</th><th>Qty</th><th>Amount</th><th>Unit Cost</th><th>Notes</th></tr></thead>
                    <tbody>
                      {history.map((h, i) => (
                        <tr key={i}>
                          <td>{new Date(h.created_at).toLocaleString()}</td>
                          <td><span className={`badge ${h.type === "IN" ? "bg-success" : "bg-danger"}`}>{h.type}</span></td>
                          <td>{h.quantity}</td>
                          <td>₹{parseFloat(h.amount).toFixed(2)}</td>
                          <td>₹{parseFloat(h.per_unit_cost).toFixed(2)}</td>
                          <td>{h.notes || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <div className="modal-footer">
                <button onClick={closeHistory} className="btn btn-secondary">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  </div>
);
};

export default InventoryManager;
